import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { submitEnquiry } from "@/lib/api";

const COPY = {
  assessment: {
    overline: "Wellness Assessment",
    titleA: "Begin your ",
    titleB: "journey.",
    desc: "A 60-minute, unhurried consultation with a Carlton wellness specialist.",
    messageLabel: "Your Wellness Goal",
    submit: "Request Consultation",
    toast: "A wellness specialist will reach out to schedule your assessment.",
  },
  franchise: {
    overline: "Franchise Enquiry",
    titleA: "Partner with ",
    titleB: "Carlton.",
    desc: "Share your details and our franchise team will connect with you within 24 hours.",
    messageLabel: "Your Message / Investment Interest",
    submit: "Submit Enquiry",
    toast: "Our franchise team will connect with you shortly.",
  },
  membership: {
    overline: "Membership Enquiry",
    titleA: "Wellness that ",
    titleB: "stays.",
    desc: "Tell us your goals and our team will help you choose the right membership.",
    messageLabel: "Your Wellness Goal",
    submit: "Enquire Now",
    toast: "Our membership team will reach out with the right plan for you.",
  },
};

const inputCls =
  "mt-2 bg-transparent border-x-0 border-t-0 border-b border-[#050505]/30 rounded-none focus-visible:ring-0 focus-visible:border-[#c9a14a] px-0 font-light";

export default function BookAssessmentDialog({ open, onOpenChange, type = "assessment" }) {
  const copy = COPY[type] || COPY.assessment;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitEnquiry({ type, ...form, source_page: window.location.pathname });
      onOpenChange(false);
      toast.success("Request received", { description: copy.toast });
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
    } catch {
      toast.error("Something went wrong", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="book-assessment-dialog"
        className="max-w-lg bg-[#f7f2e8] border-[#d8c8b8] text-[#050505] p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="p-8 md:p-10">
          <DialogHeader className="text-left space-y-3 mb-6">
            <div className="overline text-[#c9a14a]">{copy.overline}</div>
            <DialogTitle className="font-serif-display text-[#050505] text-3xl md:text-4xl leading-tight font-light">
              {copy.titleA}
              <span className="font-serif-italic text-[#c9a14a]">{copy.titleB}</span>
            </DialogTitle>
            <DialogDescription className="text-[#050505]/65 font-light">
              {copy.desc}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="name">Name</Label>
                <Input id="name" data-testid="book-name-input" required value={form.name} onChange={update("name")} className={inputCls} />
              </div>
              <div>
                <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="phone">Phone</Label>
                <Input id="phone" type="tel" data-testid="book-phone-input" required value={form.phone} onChange={update("phone")} className={inputCls} />
              </div>
            </div>
            <div>
              <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="book-email-input" required value={form.email} onChange={update("email")} className={inputCls} />
            </div>
            <div>
              <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="city">City</Label>
              <Input id="city" data-testid="book-city-input" required value={form.city} onChange={update("city")} className={inputCls} />
            </div>
            <div>
              <Label className="overline text-[#050505]/70 text-[10px]" htmlFor="message">
                {copy.messageLabel}
              </Label>
              <Textarea
                id="message"
                data-testid="book-goal-input"
                value={form.message}
                onChange={update("message")}
                rows={3}
                className="mt-2 bg-transparent border border-[#050505]/20 rounded-none focus-visible:ring-0 focus-visible:border-[#c9a14a] font-light"
              />
            </div>

            <button
              type="submit"
              data-testid="book-submit-button"
              disabled={submitting}
              className="btn-gold w-full justify-center mt-4 disabled:opacity-60"
            >
              {submitting ? "Sending…" : copy.submit}
              {!submitting && <ArrowUpRight size={16} strokeWidth={1.5} />}
            </button>

            <p className="text-[10px] uppercase tracking-[0.18em] text-[#050505]/40 text-center pt-2">
              By submitting you agree to be contacted by our team.
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
